import { useCallback, useMemo } from "react";
import { IFlightType } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import { formatFunction, parseFunction } from "./helpers";

import { useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IFlightTypeSelectorProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
}

/** Sort types list Alphabetically */
const sortTypesByDescription = (a: IFlightType, b: IFlightType): number => {
  return a.title.toLowerCase().localeCompare(b.title.toLowerCase());
};

/**
 * Sepcialized wrapper for "built-in" oc non flying type select field.
 * @param props -
 */
export const FlightTypeSelectField = (props: IFlightTypeSelectorProps): JSX.Element => {
  const { mosaicPane, label, name, onChange, valueKey, isMulti, defaultValue, ...selectProps } = props;

  const structures = useSelector((state: IRootState) => state.structures);
  const flightTypes = useMemo(() => {
    return structures?.customerStructure?.flightTypes ?? [];
  }, [structures?.customerStructure?.flightTypes]);

  const options = useMemo(() => flightTypes.sort(sortTypesByDescription), [flightTypes]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, flightTypes, valueKey, isMulti),
    [flightTypes, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Flight Type Selector"}
      isMulti={isMulti}
      options={options}
      getOptionLabel={(type: IFlightType) => `${type.title} - ${type.description}`}
      getOptionValue={(type: IFlightType) => type._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder={`Select Flight Type${isMulti && "s"}`}
      defaultValue={defaultValue}
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
